/*
 * Reusable app-page shell: topbar with brand + optional back link, a
 * Profile chip, and Sign out. The Profile chip shows the user's avatar
 * if they've uploaded one — otherwise their initial.
 *
 * When the current URL is inside an event (/app/tournaments/:eventId/...
 * including the event home itself), a second sticky row below the
 * topbar surfaces the EventTabs so the user can jump between Teams /
 * Players / Rounds / Leaderboard / Settings without scrolling back up.
 * The eventId is auto-detected — pages don't need to wire anything.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/store/authStore';
import { api } from '@/lib/api';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { BrandWordmark } from '@/components/BrandWordmark';
import { EventTabs } from '@/routes/tournaments/EventTabs';

interface Props {
  back?: { to: string; label: string };
  children: ReactNode;
  /** When true, the <main> element drops its max-width + padding and
   *  goes full-bleed. Used by the broadcast match-scoring page so the
   *  angled banner can run to the viewport edges. */
  fullBleed?: boolean;
}

/** Extract eventId from any /app/tournaments/:eventId(/...) URL. Returns
 *  null on non-event paths and on /app/tournaments (list). */
function useEventIdFromPath(): string | null {
  const loc = useLocation();
  const m = loc.pathname.match(/^\/app\/tournaments\/([^/]+)(\/|$)/);
  // Filter out reserved sub-paths that look like event ids but aren't,
  // i.e. "new". /tournaments/new is the create page, not an event.
  const candidate = m?.[1] ?? null;
  if (!candidate || candidate === 'new') return null;
  return candidate;
}

export function AppShell({ back, children, fullBleed }: Props) {
  const navigate = useNavigate();
  const clientLogout = useAuthStore((s) => s.logout);
  const accessToken = useAuthStore((s) => s.accessToken);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const impersonatingTargetName = useAuthStore((s) => s.impersonatingTargetName);
  const exitImpersonation = useAuthStore((s) => s.exitImpersonation);
  const eventId = useEventIdFromPath();

  // Pull the current user's avatar so the topbar Profile chip can show
  // it. We fetch lazily on mount and ignore failures — the chip just
  // falls back to the monogram if /me errors or hasn't loaded yet.
  const [me, setMe] = useState<{ avatarUrl: string | null; displayName: string | null } | null>(null);
  useEffect(() => {
    if (!accessToken) { setMe(null); return; }
    let cancelled = false;
    api.me()
      .then((data) => { if (!cancelled) setMe({ avatarUrl: data.avatarUrl, displayName: data.displayName }); })
      .catch(() => { /* topbar chip falls back to monogram */ });
    return () => { cancelled = true; };
  }, [accessToken]);

  async function handleLogout() {
    const refresh = useAuthStore.getState().refreshToken;
    if (refresh) api.logout(refresh).catch(() => {});
    clientLogout();
    navigate('/app/login', { replace: true });
  }

  return (
    <div className="app-shell">
      {/* Impersonation banner. Pinned above the topbar so it's
          impossible to miss the fact that the current session is
          "viewing as" someone else. Single tap on Exit restores the
          owner session. */}
      {isImpersonating && (
        <div
          role="status"
          aria-live="polite"
          style={{
            background: 'var(--gold)',
            color: '#000',
            padding: '8px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            fontWeight: 800,
            fontSize: 13,
            letterSpacing: 0.3,
            flexWrap: 'wrap',
          }}
        >
          <span>
            Viewing as <strong>{impersonatingTargetName ?? 'player'}</strong>
          </span>
          <button
            type="button"
            // AuthGate remounts account-owned screens. Reloading the document
            // would lose a non-persistent owner or hydrate another tab's login.
            onClick={exitImpersonation}
            style={{
              background: '#000',
              color: 'var(--gold)',
              border: 'none',
              padding: '8px 14px',
              minHeight: 36,
              borderRadius: 6,
              fontWeight: 800,
              fontSize: 12,
              cursor: 'pointer',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}
          >
            Exit
          </button>
        </div>
      )}
      <header className="app-topbar">
        <div className="app-topbar-inner">
          {back ? (
            <Link to={back.to} className="t-topbar-back">
              ← {back.label}
            </Link>
          ) : (
            // Brand link. When signed in, send the user to /app/home
            // (which keeps the SPA mounted and preserves auth state). The
            // old `<a href="/">` did a full reload to the marketing site
            // which made it look like the user was signed out — they
            // weren't, but the marketing site is a separate worker route
            // that always renders the public landing page. Use <Link>
            // when authed so we never leave the SPA.
            accessToken ? (
              <Link to="/app/home" className="app-brand">
                <BrandWordmark className="brand-wordmark" />
              </Link>
            ) : (
              <a href="/" className="app-brand">
                <BrandWordmark className="brand-wordmark" />
              </a>
            )
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {accessToken && (
              <Link
                to="/app/profile"
                title="Your profile"
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  textDecoration: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: 999,
                  padding: 5,
                  background: 'transparent',
                }}
              >
                <PlayerAvatar avatarUrl={me?.avatarUrl} displayName={me?.displayName} size={28} />
              </Link>
            )}
            <button onClick={handleLogout} className="app-topbar-action">
              Sign out
            </button>
          </div>
        </div>
        {eventId && (
          <div className="app-topbar-tabs">
            <div className="app-topbar-tabs-inner">
              <EventTabs eventId={eventId} />
            </div>
          </div>
        )}
      </header>
      <main className={fullBleed ? 'app-main app-main-fullbleed' : 'app-main'}>{children}</main>
    </div>
  );
}
